import { createTheme } from "@mui/material/styles";
import type { PaletteOptions, ThemeOptions } from "@mui/material/styles";

declare module "@mui/material/styles" {
    interface Palette {
        custom?: Palette["primary"];
        brand?: Palette["primary"];
    }
    interface PaletteOptions {
        custom?: PaletteOptions["primary"];
        brand?: PaletteOptions["primary"];
    }
    interface TypographyVariants {
        pageTitle: React.CSSProperties;
    }
    interface TypographyVariantsOptions {
        pageTitle: React.CSSProperties;
    }
}

declare module "@mui/material/Typography" {
    interface TypographyPropsVariantOverrides {
        pageTitle: true;
    }
}

/**
 * The purple behind the Daifuku logo, used for the nav chrome (side drawer
 * and its toolbar header) in both color modes, and as `primary` in the light
 * theme so buttons, checkboxes, etc. match the sidebar and the logo. The dark
 * theme keeps a lighter `primary` because this purple lacks contrast against
 * dark backgrounds.
 */
const brandPalette = createTheme().palette.augmentColor({
    color: { main: "#51479c" },
});

const darkModePalette: PaletteOptions = {
    mode: "dark",
    brand: brandPalette,
    primary: {
        main: "#9c5eff",
    },
    secondary: {
        main: "#4672f9",
    },
    success: {
        main: "#00b500",
        contrastText: "#fff",
    },
    error: {
        main: "#CA3838",
    },
    warning: {
        main: "#E6A700",
    },
};

const lightModePalette: PaletteOptions = {
    mode: "light",
    brand: brandPalette,
    action: {
        selectedOpacity: 0.3,
    },
    primary: {
        main: brandPalette.main,
    },
    secondary: {
        main: "#4672f9",
    },
    success: {
        main: "#00b500",
        contrastText: "#fff",
    },
    error: {
        main: "#CA3838",
    },
    warning: {
        main: "#E68A00",
    },
};

export const themeObj: ThemeOptions = {
    components: {
        MuiAppBar: {
            defaultProps: {
                color: "inherit",
                enableColorOnDark: true,
            },
            variants: [
                {
                    props: { color: "inherit" },
                    style: (theme) =>
                        theme.theme.palette.mode === "dark"
                            ? {}
                            : {
                                  backgroundColor:
                                      theme.theme.palette.grey[300],
                              },
                },
            ],
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    padding: "1rem 0.75rem",
                },
                // Contained buttons will generate a border with the same color
                // to match the size of outlined buttons
                contained: (() => {
                    return {
                        borderWidth: "1px",
                    };
                }) as any,
            },
        },
        MuiChip: {
            variants: [
                {
                    props: { color: "default", variant: "filled" },
                    style: (theme) => {
                        if (theme.theme.palette.mode === "dark") return {};

                        const color = "#bababa";
                        return {
                            backgroundColor: color,
                            color: theme.theme.palette.getContrastText(color),
                        };
                    },
                },
            ],
        },
        MuiContainer: {
            defaultProps: {
                maxWidth: "xl",
            },
        },
        MuiDivider: {
            variants: [
                {
                    props: {},
                    style: (theme) =>
                        theme.theme.palette.mode === "dark"
                            ? {}
                            : {
                                  backgroundColor:
                                      theme.theme.palette.grey[300],
                              },
                },
            ],
            styleOverrides: {
                root: (theme) =>
                    theme.theme.palette.mode === "dark"
                        ? {}
                        : {
                              backgroundColor: theme.theme.palette.grey[300],
                          },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: "12px",
                    marginBottom: "12px",
                },
            },
        },
        MuiGrid: {
            defaultProps: {
                spacing: 1,
            },
        },
        MuiPaper: {
            variants: [
                {
                    props: { elevation: 0, variant: "elevation" },
                    style: (theme) =>
                        theme.theme.palette.mode === "light" ? {} : {},
                },
                {
                    props: { elevation: 1, variant: "elevation" },
                    style: (theme) =>
                        theme.theme.palette.mode === "light"
                            ? {
                                  boxShadow: theme.theme.shadows[3],
                              }
                            : {},
                },
                {
                    props: { elevation: 2, variant: "elevation" },
                    style: (theme) =>
                        theme.theme.palette.mode === "light"
                            ? {
                                  boxShadow: theme.theme.shadows[3],
                              }
                            : {},
                },
            ],
        },
        MuiStack: {
            variants: [
                {
                    props: { spacing: 0 },
                    style: {
                        gap: 1,
                    },
                },
            ],
        },
        MuiTypography: {
            styleOverrides: {
                subtitle2: (theme) => ({
                    fontSize: "0.75rem",
                    fontWeight: "initial",
                    color: theme.theme.palette.text.disabled,
                }),
                h3: () => ({
                    textAlign: "start",
                }),
            },
            defaultProps: {
                variantMapping: {
                    subtitle2: "span",
                },
            },
        },
    },
    typography: {
        pageTitle: {
            fontSize: "1.25rem",
            fontWeight: 500,
        },
        fontFamily:
            "'InterVariable', 'Inter var', 'Inter', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Segoe UI', Oxygen, Ubuntu, Cantarell, 'Open Sans', sans-serif",
    },
};

export const theme = {
    dark: { ...themeObj, palette: darkModePalette },
    light: { ...themeObj, palette: lightModePalette },
} as {
    dark: ThemeOptions;
    light: ThemeOptions;
};
